import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import React from 'react';

import {
  useGetAppBbWaardecheckQuery,
  useUpdateAppBbWaardecheckMutation,
  useGetAppBbWaardecheckRouteAvailabilityLazyQuery,
} from '~/graphql/types';
import BndlLoading from '~/components/atom/BndlLoading';
import { useRecoilState, useSetRecoilState } from 'recoil';
import formatToastMessage from '~/util/formatToastMessage';
import useAddToast from '~/hooks/useAddToast';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import ErrorScreen from '~/components/page/ErrorScreen';
import cleanedFilename from '~/util/cleanedFilename';
import WaardecheckTemplate, {
  StateId,
  WaardecheckApp,
  getImageStash,
} from '../components/WaardecheckTemplate';
import transformToInput from '../components/WaardecheckTemplate/utils/transformToInput';
import useErrorReporter from '~/hooks/useErrorReporter';
import useApp from '~/hooks/useApp';
import {
  appWaardecheckImageStash,
  appWaardecheckState,
} from '../components/WaardecheckTemplate/state';
import NotFound from '../../404';
import Catalog from '~/Catalog';

const text = {
  updateSuccess: Catalog.genericSuccessMessage,
  appTitle: 'Waardecheck - Powered by Brainbay',
  updateError: 'Opslaan niet gelukt door foutieve waarden in het formulier',
};

type Props = RouteComponentProps & {};

const BBWaardecheck: React.FCC<Props> = ({ uri = '' }) => {
  const { id: accountId } = useCurrentAccount();
  const reporter = useErrorReporter();

  const {
    app,
    loading: appLoading,
    error: appError,
  } = useApp('AppStatus_BBWaardecheck');

  const [, setOriginal] = useRecoilState(appWaardecheckState(StateId.original));

  const [updated, setUpdated] = useRecoilState(
    appWaardecheckState(StateId.updated),
  );

  const addToast = useAddToast();

  const setImageStash = useSetRecoilState(appWaardecheckImageStash);

  const { data, error, loading, updateQuery } = useGetAppBbWaardecheckQuery({
    variables: { accountId },
    onCompleted: ({ getAppBBWaardecheck }) => {
      if (!getAppBBWaardecheck) return;

      const inputFormatted = transformToInput(getAppBBWaardecheck);

      setUpdated(inputFormatted);
      setOriginal(inputFormatted);
      setImageStash(getImageStash(getAppBBWaardecheck));
    },
  });

  const [updateAppBbWaardecheck, { loading: updating }] =
    useUpdateAppBbWaardecheckMutation();

  const [
    getRouteAvailability,
    {
      loading: getRouteAvailabilityLoading,
      data: getRouteAvailabilityData,
      variables: getRouteAvailabilityDataVariables,
    },
  ] = useGetAppBbWaardecheckRouteAvailabilityLazyQuery();

  if (appLoading) return <BndlLoading />;
  if (!app?.appStatus.enabled) return <NotFound />;

  if (error || appError) {
    reporter.captureException(
      new Error(
        `${cleanedFilename(__filename)} | An error occured (${JSON.stringify(
          error || appError,
        )})`,
      ),
    );

    return <ErrorScreen />;
  }

  if (!data || loading || !updated) return <BndlLoading />;

  return (
    <WaardecheckTemplate
      uri={uri}
      appTitle={text.appTitle}
      appType={WaardecheckApp.BB}
      queryData={data.getAppBBWaardecheck}
      queryLoading={loading}
      mutationLoading={updating}
      onSave={async () => {
        const { errors, data } = await updateAppBbWaardecheck({
          variables: {
            accountId,
            update: updated,
          },
        });

        if (errors || !data) {
          reporter.captureException(
            new Error(JSON.stringify(errors || 'No Data')),
            'fatal',
          );
          addToast([formatToastMessage(text.updateError, 'danger')]);
          return;
        }

        addToast([formatToastMessage(text.updateSuccess, 'success')]);

        const { updateAppBBWaardecheck } = data;

        updateQuery(data => ({
          ...data,
          getAppBBWaardecheck: updateAppBBWaardecheck,
        }));

        return updateAppBBWaardecheck;
      }}
      routeAvailability={{
        loading: getRouteAvailabilityLoading,
        data: getRouteAvailabilityData,
        variables: getRouteAvailabilityDataVariables,
      }}
      getRouteAvailability={route =>
        getRouteAvailability({
          variables: {
            accountId,
            route,
          },
        })
      }
    />
  );
};

export default BBWaardecheck;
